// MAGAZINE skin — the two layouts of the category list screens, plus the
// editorial count kicker above them:
//   • GALLERY — a two-column masonry of colour-field tiles (status sticker,
//     rating star, big Anton title, optional year/award pill).
//   • LIST — a vertical stack of glass rows (46×62 cover, sticker + year kicker,
//     Anton title, per-media subline, ★ rating), the "Continue watching" shape.
// `renderCategoryEntries` renders either from the same sorted entries; groups
// render inline in both, and every node carries data-drag-id/kind so
// drag-to-group works regardless of layout (utils/drag-drop.js).
import { el } from "./dom.js";
import { statusLabel, awardLabel } from "./status-badge.js";
import { magCat, magTileBg, yearOf, rowSubline, MAG } from "../utils/magazine.js";
import { magGlass, magCover } from "./magazine/kit.js";
import { groupCoverUrl } from "./group-card.js";

// Sticker colour by engagement status; anything else gets a translucent chip.
const STICKER_STYLE = {
	playing: { bg: MAG.lime, color: "#0b0b0f" },
	replaying: { bg: MAG.yellow, color: "#0b0b0f" },
	done: { bg: "#ffffff", color: "#0b0b0f" },
};
const STICKER_DEFAULT = { bg: "rgba(255,255,255,.16)", color: "#ffffff" };

// Heights cycle to give the gallery its staggered rhythm. The gallery is a
// 2-column grid filling row by row, so both tiles in a row must share a height
// or the shorter one leaves a gap — `tileHeight` keys off the ROW (index ÷ 2)
// rather than the tile, keeping rows flush while the height still varies down
// the page.
const TILE_HEIGHTS = [218, 138, 162, 158, 222, 136];
function tileHeight(index) {
	const row = Math.floor(index / 2);
	return TILE_HEIGHTS[row % TILE_HEIGHTS.length];
}

// Per-category kicker phrase ("112 in the vault").
const KICKER_PHRASE = {
	videogame: "in the vault",
	book: "on the shelf",
	tv: "in the lineup",
	anime: "in the lineup",
	movie: "on the marquee",
	comic: "in the longbox",
};

// The editorial count line that sits above the gallery.
export function magKicker(count, mediaType) {
	const cat = magCat(mediaType);
	return el(
		"div",
		{ style: { padding: "14px 4px 10px" } },
		el("span", {
			style: {
				fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "10px",
				letterSpacing: ".12em", textTransform: "uppercase", color: cat.color,
			},
		}, `${count} ${KICKER_PHRASE[mediaType] || "collected"}`),
	);
}

// A GROUP rendered as a gallery tile — same shape as an item tile so a
// collection sits inline among the covers, but marked with a lime "GROUP"
// sticker + member count instead of a status. The cover is a representative
// member's art (via groupCoverUrl); the stacked look reads as "a set".
export function magGroupTile(group, members, { onClick, index = 0 } = {}) {
	const height = tileHeight(index);
	const tall = height >= 180;
	const item = { cover_url: groupCoverUrl(group, members), media_type: group.media_type };

	const sticker = el("span", {
		style: {
			position: "absolute", top: "9px", left: "9px",
			fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "7.5px",
			letterSpacing: ".1em", textTransform: "uppercase", color: "#0b0b0f",
			background: MAG.lime, padding: "3px 7px", transform: "skewX(-7deg)",
		},
	}, `◆ Group · ${members.length}`);

	const bottom = el(
		"div",
		{ style: { position: "absolute", left: "10px", right: "10px", bottom: "9px" } },
		el("div", {
			style: {
				fontFamily: "'Anton', sans-serif", fontSize: tall ? "24px" : "19px",
				lineHeight: ".85", textTransform: "uppercase", color: "#fff",
				textShadow: "0 2px 10px rgba(0,0,0,.4)",
				display: "-webkit-box", webkitLineClamp: "3", webkitBoxOrient: "vertical", overflow: "hidden",
			},
		}, group.name),
	);

	return el(
		"div",
		{
			role: "button",
			onclick: onClick,
			style: {
				position: "relative", height: `${height}px`, borderRadius: "14px", overflow: "hidden",
				cursor: "pointer", boxShadow: "0 8px 22px rgba(0,0,0,.45)", background: magTileBg(item),
				outline: `2px solid ${MAG.lime}`, outlineOffset: "-2px",
			},
		},
		sticker,
		bottom,
	);
}

export function magGalleryTile(item, { mediaType, onClick, index = 0 } = {}) {
	const cat = magCat(mediaType);
	const height = tileHeight(index);
	const tall = height >= 180;

	const sticker = STICKER_STYLE[item.engagement_status] || STICKER_DEFAULT;
	const stickerLabel = statusLabel(item.engagement_status, mediaType);

	const children = [];

	// status sticker (top-left)
	if (item.engagement_status) {
		children.push(
			el("span", {
				style: {
					position: "absolute", top: "9px", left: "9px",
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "7.5px",
					letterSpacing: ".08em", textTransform: "uppercase",
					color: sticker.color, background: sticker.bg, padding: "3px 6px",
					transform: "skewX(-7deg)",
				},
			}, stickerLabel),
		);
	}

	// "On air" (roadmap #43): TV / anime with episodes airing around now.
	if ((mediaType === "tv" || mediaType === "anime") && isAiring(item, "now")) {
		children.push(
			el("span", {
				style: {
					position: "absolute", top: item.engagement_status ? "27px" : "9px", left: "9px",
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "7.5px",
					letterSpacing: ".08em", textTransform: "uppercase",
					color: "#fff", background: "#e5383b", padding: "3px 6px", transform: "skewX(-7deg)",
				},
			}, "● On air"),
		);
	}

	// rating star (top-right)
	if (item.rating != null) {
		children.push(
			el("div", {
				style: {
					position: "absolute", top: "9px", right: "9px",
					fontFamily: "'Anton', sans-serif", fontSize: "15px", color: MAG.yellow,
					textShadow: "0 1px 6px rgba(0,0,0,.5)",
				},
			}, `★${item.rating}`),
		);
	}

	// bottom block: title, then an optional year/award meta pill
	const year = yearOf(item);
	const award = (item.awards ?? [])[0];
	const metaText = [year ? String(year) : null, award ? awardLabel(award) : null]
		.filter(Boolean)
		.join(" · ");

	const bottom = el(
		"div",
		{ style: { position: "absolute", left: "10px", right: "10px", bottom: "9px", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "6px" } },
		el("div", {
			style: {
				fontFamily: "'Anton', sans-serif", fontSize: tall ? "25px" : "20px",
				lineHeight: ".85", textTransform: "uppercase", color: "#fff",
				textShadow: "0 2px 10px rgba(0,0,0,.4)",
				display: "-webkit-box", webkitLineClamp: "3", webkitBoxOrient: "vertical", overflow: "hidden",
			},
		}, item.title),
	);
	if (metaText) {
		bottom.append(
			el("span", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "7.5px",
					letterSpacing: ".08em", textTransform: "uppercase", color: "#fff",
					background: "rgba(0,0,0,.35)", backdropFilter: "blur(8px)", webkitBackdropFilter: "blur(8px)",
					border: `1px solid ${MAG.line}`, borderRadius: "999px", padding: "4px 9px",
				},
			}, metaText),
		);
	}
	children.push(bottom);

	return el(
		"div",
		{
			role: "button",
			onclick: onClick,
			style: {
				position: "relative", height: `${height}px`, borderRadius: "14px", overflow: "hidden",
				cursor: "pointer", boxShadow: "0 8px 22px rgba(0,0,0,.45)", background: magTileBg(item),
			},
		},
		...children,
	);
}

// ---- LIST layout -------------------------------------------------------------

const ROW_KICKER_STYLE = {
	fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "9px",
	letterSpacing: ".06em", textTransform: "uppercase", color: MAG.dim,
};
const ROW_TITLE_STYLE = {
	fontFamily: "'Anton', sans-serif", fontSize: "18px", lineHeight: ".95",
	textTransform: "uppercase", color: "#fff",
	overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};
const ROW_SUB_STYLE = {
	fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "11px",
	color: MAG.dim, marginTop: "3px",
	overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};

// The skewed sticker chip, inline (same colours as the tile's corner sticker).
function rowChip(text, { bg, color }) {
	return el("span", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "7.5px",
			letterSpacing: ".08em", textTransform: "uppercase", color, background: bg,
			padding: "3px 6px", transform: "skewX(-7deg)", flex: "none",
		},
	}, text);
}

// The 9px-gap column the rows stack in (matches Home's "Continue watching").
export function magList(children = []) {
	return el(
		"div",
		{ style: { display: "flex", flexDirection: "column", gap: "9px" } },
		...children,
	);
}

// One item as a glass row.
export function magListRow(item, { mediaType, onClick } = {}) {
	const sticker = STICKER_STYLE[item.engagement_status] || STICKER_DEFAULT;
	const year = yearOf(item);
	const kicker = el(
		"div",
		{ style: { display: "flex", alignItems: "center", gap: "7px", marginBottom: "4px", minWidth: "0" } },
		item.engagement_status
			? rowChip(statusLabel(item.engagement_status, mediaType), sticker)
			: null,
		year ? el("span", { style: ROW_KICKER_STYLE }, String(year)) : null,
	);
	const sub = rowSubline(item, mediaType);
	const body = el(
		"div",
		{ style: { flex: "1", minWidth: "0" } },
		kicker,
		el("div", { style: ROW_TITLE_STYLE }, item.title ?? ""),
		sub ? el("div", { style: ROW_SUB_STYLE }, sub) : null,
	);
	const rating = item.rating != null
		? el("div", {
			style: { fontFamily: "'Anton', sans-serif", fontSize: "17px", color: MAG.yellow, flex: "none" },
		}, `★${item.rating}`)
		: null;

	return magGlass(
		[
			magCover({ ...item, media_type: mediaType }, { height: "62px", width: "46px", radius: "8px" }),
			body,
			rating,
		],
		{
			role: "button",
			onClick,
			style: { display: "flex", alignItems: "center", gap: "12px", padding: "10px 12px" },
		},
	);
}

// A GROUP as a glass row — lime outline + "◆ Group · N" chip instead of a
// status, member titles as the subline. Same cover choice as the tile.
export function magGroupListRow(group, members, { onClick } = {}) {
	const item = { cover_url: groupCoverUrl(group, members), media_type: group.media_type };
	const names = members.slice(0, 3).map((m) => m.title).filter(Boolean).join(" · ");
	const more = members.length > 3 ? ` +${members.length - 3}` : "";
	const body = el(
		"div",
		{ style: { flex: "1", minWidth: "0" } },
		el(
			"div",
			{ style: { display: "flex", alignItems: "center", gap: "7px", marginBottom: "4px" } },
			rowChip(`◆ Group · ${members.length}`, { bg: MAG.lime, color: "#0b0b0f" }),
		),
		el("div", { style: ROW_TITLE_STYLE }, group.name ?? ""),
		names ? el("div", { style: ROW_SUB_STYLE }, names + more) : null,
	);
	return magGlass(
		[magCover(item, { height: "62px", width: "46px", radius: "8px" }), body],
		{
			role: "button",
			onClick,
			style: {
				display: "flex", alignItems: "center", gap: "12px", padding: "10px 12px",
				outline: `2px solid ${MAG.lime}`, outlineOffset: "-2px",
			},
		},
	);
}

// ---- shared renderer ---------------------------------------------------------

// Render a category's sorted `entries` ([{ item } | { group, members }]) into
// `container` as either layout. `count` is the number of items (drives the
// kicker line); `empty()` builds the empty state when there is nothing to show.
export function renderCategoryEntries(
	container,
	entries,
	{ mediaType, view = "gallery", count = 0, onOpenItem, onOpenGroup, empty },
) {
	if (count > 0) container.append(magKicker(count, mediaType));
	if (entries.length === 0) {
		if (empty) container.append(empty());
		return;
	}
	const nodes = entries.map((entry, index) => {
		let node;
		if (entry.group) {
			node = view === "list"
				? magGroupListRow(entry.group, entry.members, { onClick: () => onOpenGroup?.(entry.group) })
				: magGroupTile(entry.group, entry.members, { onClick: () => onOpenGroup?.(entry.group), index });
			node.dataset.dragId = entry.group.id;
			node.dataset.dragKind = "group";
		} else {
			node = view === "list"
				? magListRow(entry.item, { mediaType, onClick: () => onOpenItem?.(entry.item) })
				: magGalleryTile(entry.item, { mediaType, onClick: () => onOpenItem?.(entry.item), index });
			node.dataset.dragId = entry.item.id;
			node.dataset.dragKind = "item";
		}
		return node;
	});
	container.append(view === "list" ? magList(nodes) : el("div.mag-gallery", {}, ...nodes));
}
